// Mock API service for forecast functionality using localStorage

import {
  ForecastProject,
  TurbineForForecast,
  ListForecastsResponse,
  StoredForecastData
} from '@/types/forecast';
import { FailureFilters } from '@/types/filters';
import { failuresApi } from './failures';

// Storage keys
const FORECAST_STORAGE_KEY = 'weibull_forecasts';
const FORECAST_STORAGE_VERSION = '1.0.0';

// Mock user ID (in real app, this would come from auth)
const MOCK_USER_ID = 'mock_user_123';

/**
 * Mock API service for forecasts using localStorage
 */
export const mockForecastApi = {
  /**
   * Get turbines matching forecast filters for population calculation
   */
  async getTurbinesForForecast(filters: FailureFilters): Promise<TurbineForForecast[]> {
    try {
      console.log('Getting turbines for forecast with filters:', filters);

      // Get failure data that matches the filters
      const failuresResponse = await failuresApi.getFailuresForWeibullAnalysis(filters, false, null);

      if (!failuresResponse.failures || failuresResponse.failures.length === 0) {
        console.log('No failures found for filters');
        return [];
      }

      // Create unique turbine list with installation dates
      const turbineMap = new Map<string, TurbineForForecast>();

      failuresResponse.failures.forEach(failure => {
        const turbineKey = `${failure.turbine_name}_${failure.site_name}`;

        if (!turbineMap.has(turbineKey)) {
          // Mock installation date based on turbine name (in real app, this would come from database)
          const installDate = this.mockInstallDate(failure.turbine_name, failure.site_name);
          const ageMonths = this.calculateAgeMonths(installDate);

          turbineMap.set(turbineKey, {
            turbineId: failure.turbine_name,
            siteName: failure.site_name,
            installationDate: installDate,
            turbineModel: null, // Mock doesn't have model info
            failures: [], // Will be populated below
            currentAge: ageMonths / 12 // Convert months to years
          });
        }
      });

      const turbines = Array.from(turbineMap.values());
      console.log(`Found ${turbines.length} unique turbines for forecast`);
      return turbines;
    } catch (error) {
      console.error('Error getting turbines for forecast:', error);
      throw error;
    }
  },

  /**
   * Save forecast workspace to localStorage
   */
  async saveForecast(workspace: ForecastProject): Promise<ForecastProject> {
    try {
      console.log('Saving forecast workspace:', workspace.name);

      const data = this.getStoredData();

      // Check for duplicate names
      const existingNames = data.forecasts
        .filter(f => f.metadata.userId === MOCK_USER_ID)
        .map(f => f.name);

      if (existingNames.includes(workspace.name)) {
        throw new Error(`Forecast named "${workspace.name}" already exists`);
      }

      // Add to storage
      data.forecasts.push({
        ...workspace,
        metadata: {
          ...workspace.metadata,
          userId: MOCK_USER_ID,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      });

      this.saveStoredData(data);
      console.log(`Forecast workspace saved: ${workspace.name}`);
      return workspace;
    } catch (error) {
      console.error('Error saving forecast:', error);
      throw error;
    }
  },

  /**
   * Load forecast workspace by ID
   */
  async loadForecast(id: string): Promise<ForecastProject> {
    try {
      const data = this.getStoredData();
      const forecast = data.forecasts.find(f => f.id === id && f.metadata.userId === MOCK_USER_ID);

      if (!forecast) {
        throw new Error(`Forecast with ID ${id} not found`);
      }

      console.log(`Forecast workspace loaded: ${forecast.name}`);
      return forecast;
    } catch (error) {
      console.error('Error loading forecast:', error);
      throw error;
    }
  },

  /**
   * Update existing forecast workspace
   */
  async updateForecast(id: string, updates: Partial<ForecastProject>): Promise<ForecastProject> {
    try {
      const data = this.getStoredData();
      const index = data.forecasts.findIndex(f => f.id === id && f.metadata.userId === MOCK_USER_ID);

      if (index === -1) {
        throw new Error(`Forecast with ID ${id} not found`);
      }

      // Update forecast
      data.forecasts[index] = {
        ...data.forecasts[index],
        ...updates,
        metadata: {
          ...data.forecasts[index].metadata,
          ...updates.metadata,
          updatedAt: new Date().toISOString(),
          version: (data.forecasts[index].metadata.version || 1) + 1
        }
      };

      this.saveStoredData(data);
      console.log(`Forecast workspace updated: ${data.forecasts[index].name}`);
      return data.forecasts[index];
    } catch (error) {
      console.error('Error updating forecast:', error);
      throw error;
    }
  },

  /**
   * List all forecast workspaces for current user
   */
  async listForecasts(): Promise<ListForecastsResponse> {
    try {
      const data = this.getStoredData();
      const userForecasts = data.forecasts.filter(f => f.metadata.userId === MOCK_USER_ID);

      // Sort by updatedAt desc
      userForecasts.sort((a, b) =>
        new Date(b.metadata.updatedAt).getTime() - new Date(a.metadata.updatedAt).getTime()
      );

      console.log(`Found ${userForecasts.length} forecast workspaces`);
      return {
        forecasts: userForecasts,
        total: userForecasts.length
      };
    } catch (error) {
      console.error('Error listing forecasts:', error);
      throw error;
    }
  },

  /**
   * Delete forecast workspace
   */
  async deleteForecast(id: string): Promise<void> {
    try {
      const data = this.getStoredData();
      const index = data.forecasts.findIndex(f => f.id === id && f.metadata.userId === MOCK_USER_ID);

      if (index === -1) {
        throw new Error(`Forecast with ID ${id} not found`);
      }

      const forecastName = data.forecasts[index].name;
      data.forecasts.splice(index, 1);
      this.saveStoredData(data);

      console.log(`Forecast workspace deleted: ${forecastName}`);
    } catch (error) {
      console.error('Error deleting forecast:', error);
      throw error;
    }
  },

  /**
   * Get stored forecast data from localStorage
   */
  getStoredData(): StoredForecastData {
    try {
      const stored = localStorage.getItem(FORECAST_STORAGE_KEY);
      if (!stored) {
        return this.createEmptyStorage();
      }

      const data = JSON.parse(stored) as StoredForecastData;

      // Handle version migration if needed
      if (data.version !== FORECAST_STORAGE_VERSION) {
        console.log('Migrating forecast storage to new version');
        return this.migrateStorage(data);
      }

      return data;
    } catch (error) {
      console.error('Error reading forecast storage, creating new:', error);
      return this.createEmptyStorage();
    }
  },

  /**
   * Save forecast data to localStorage
   */
  saveStoredData(data: StoredForecastData): void {
    try {
      data.lastUpdated = new Date().toISOString();
      localStorage.setItem(FORECAST_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving forecast data:', error);
      throw new Error('Failed to save forecast data. Storage may be full.');
    }
  },

  /**
   * Create empty storage structure
   */
  createEmptyStorage(): StoredForecastData {
    return {
      forecasts: [],
      version: FORECAST_STORAGE_VERSION,
      lastUpdated: new Date().toISOString()
    };
  },

  /**
   * Migrate storage to new version (placeholder for future migrations)
   */
  migrateStorage(_oldData: unknown): StoredForecastData {
    // For now, just create new storage if version mismatch
    console.warn('Storage migration not implemented, creating new storage');
    return this.createEmptyStorage();
  },

  /**
   * Mock installation date based on turbine and site (for realistic ages)
   */
  mockInstallDate(turbineName: string, siteName: string): string {
    // Create consistent but varied installation dates based on turbine/site names
    const hash = this.simpleHash(turbineName + siteName);

    // Random date between 2010 and 2022 (2-15 years old)
    const minYear = 2010;
    const maxYear = 2022;
    const year = minYear + (hash % (maxYear - minYear + 1));

    // Random month
    const month = (hash * 7) % 12 + 1; // 1-12

    // Random day (simplified to 15th of month)
    const day = 15;

    const installDate = new Date(year, month - 1, day);
    return installDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
  },

  /**
   * Calculate current age in months from installation date
   */
  calculateAgeMonths(installDateString: string): number {
    const installDate = new Date(installDateString);
    const now = new Date();

    const years = now.getFullYear() - installDate.getFullYear();
    const months = now.getMonth() - installDate.getMonth();

    return years * 12 + months;
  },

  /**
   * Simple hash function for consistent randomization
   */
  simpleHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }
};
